import React, { useMemo, useState } from 'react';
import FilteredPostsDisplay from './FilteredPostsDisplay';
import HistogramWidget from './HistogramWidget';
import ScatterPlotWidget from './ScatterPlotWidget';
import '../styles/CustomAssessmentDisplay.css';
import '../styles/HistogramWidget.css';
import '../styles/ScatterPlotWidget.css';

// Helper function to calculate percentile
const percentile = (arr, p) => {
    if (arr.length === 0) return null;
    arr.sort((a, b) => a - b);
    const index = (p / 100) * (arr.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index - lower;
    if (upper === lower) return arr[lower];
    return arr[lower] * (1 - weight) + arr[upper] * weight;
};

// Placeholder for t-distribution critical value
const T_CRITICAL_VALUE = 1.96; 

const InterestRateDisplay = ({ data }) => {
    const [selectedType, setSelectedType] = useState(null);
    const [selectedBin, setSelectedBin] = useState(null);
    const [showPostDisplay, setShowPostDisplay] = useState(true);
    const [selectedScatterPost, setSelectedScatterPost] = useState(null);

    // Process data for interest rate histogram
    const interestRateData = useMemo(() => {
        if (!data?.interest_rate || data.interest_rate.length === 0) return null;

        // Filter out null ratings first
        const validRatings = data.interest_rate.filter(d => d.rating !== null);
        if (validRatings.length === 0) return null;

        const binSize = 100; // 1% intervals (in basis points)
        const bins = {};
        
        // Find min and max for complete range
        const minRate = Math.floor(Math.min(...validRatings.map(d => d.rating)) / binSize) * binSize;
        const maxRate = Math.ceil(Math.max(...validRatings.map(d => d.rating)) / binSize) * binSize;
        
        // Initialize all bins to ensure complete range
        for (let bin = minRate; bin <= maxRate; bin += binSize) {
            bins[bin] = 0;
        }
        
        // Fill in actual data
        validRatings.forEach(d => {
            const bin = Math.floor(d.rating / binSize) * binSize;
            bins[bin] = (bins[bin] || 0) + 1;
        });

        return Object.entries(bins)
            .map(([bin, count]) => ({
                rateRange: `${Math.round(parseInt(bin)/100)}% - ${Math.round((parseInt(bin) + binSize)/100)}%`,
                count: count,
                binStart: parseInt(bin)
            }))
            .sort((a, b) => a.binStart - b.binStart);
    }, [data?.interest_rate]);

    // Process data for credit score histogram
    const creditScoreData = useMemo(() => {
        if (!data?.credit_score || data.credit_score.length === 0) return null;

        // Filter out null ratings first
        const validRatings = data.credit_score.filter(d => d.rating !== null);
        if (validRatings.length === 0) return null;

        const binSize = 50; // 50 point intervals
        const bins = {};
        
        // Find min and max for complete range
        const minScore = Math.floor(Math.min(...validRatings.map(d => d.rating)) / binSize) * binSize;
        const maxScore = Math.ceil(Math.max(...validRatings.map(d => d.rating)) / binSize) * binSize;
        
        // Initialize all bins to ensure complete range
        for (let bin = minScore; bin <= maxScore; bin += binSize) {
            bins[bin] = 0;
        }
        
        // Fill in actual data
        validRatings.forEach(d => {
            const bin = Math.floor(d.rating / binSize) * binSize;
            bins[bin] = (bins[bin] || 0) + 1;
        });

        return Object.entries(bins)
            .map(([bin, count]) => ({
                scoreRange: `${parseInt(bin)} - ${parseInt(bin) + binSize}`,
                count: count,
                binStart: parseInt(bin)
            }))
            .sort((a, b) => a.binStart - b.binStart);
    }, [data?.credit_score]);

    // Process data for interest rate spread histogram
    const spreadData = useMemo(() => {
        if (!data?.interest_rate_spread || data.interest_rate_spread.length === 0) return null;

        // Filter out null ratings first
        const validRatings = data.interest_rate_spread.filter(d => d.rating !== null);
        if (validRatings.length === 0) return null;

        const binSize = 100; // 1% intervals (in basis points)
        const bins = {};
        
        // Find min and max for complete range
        const minSpread = Math.floor(Math.min(...validRatings.map(d => d.rating)) / binSize) * binSize;
        const maxSpread = Math.ceil(Math.max(...validRatings.map(d => d.rating)) / binSize) * binSize;
        
        // Initialize all bins to ensure complete range
        for (let bin = minSpread; bin <= maxSpread; bin += binSize) {
            bins[bin] = 0;
        }
        
        // Fill in actual data
        validRatings.forEach(d => {
            const bin = Math.floor(d.rating / binSize) * binSize;
            bins[bin] = (bins[bin] || 0) + 1;
        });

        return Object.entries(bins)
            .map(([bin, count]) => ({
                spreadRange: `${Math.round(parseInt(bin)/100)}% - ${Math.round((parseInt(bin) + binSize)/100)}%`,
                count: count,
                binStart: parseInt(bin),
                isPositive: parseInt(bin) >= 0
            }))
            .sort((a, b) => a.binStart - b.binStart);
    }, [data?.interest_rate_spread]);

    // Process data for pricing receptiveness histogram
    const pricingData = useMemo(() => {
        if (!data?.pricing_receptiveness || data.pricing_receptiveness.length === 0) return null;

        // Filter out null ratings first
        const validRatings = data.pricing_receptiveness.filter(d => d.rating !== null);
        if (validRatings.length === 0) return null;

        const binSize = 2; // 2-point intervals on -10 to 10 scale
        const bins = {};
        
        // We'll use a -10 to 10 scale with bins of size 2
        const minRating = -10;
        const maxRating = 10;
        
        // Initialize all bins to ensure complete range
        for (let bin = minRating; bin < maxRating; bin += binSize) {
            bins[bin] = 0;
        }
        
        // Fill in actual data
        validRatings.forEach(d => {
            // Ensure rating is within our scale
            const clampedRating = Math.max(minRating, Math.min(maxRating, d.rating));
            const bin = Math.floor(clampedRating / binSize) * binSize;
            bins[bin] = (bins[bin] || 0) + 1;
        });

        return Object.entries(bins)
            .map(([bin, count]) => {
                const binStart = parseInt(bin);
                const binEnd = binStart + binSize;
                return {
                    receptivenessRange: `${binStart} to ${binEnd}`,
                    count: count,
                    binStart: binStart,
                    sentiment: 
                        binStart >= 7 ? 'very-receptive' :
                        binStart >= 3 ? 'receptive' :
                        binStart > -3 ? 'neutral' :
                        binStart > -7 ? 'resistant' : 'very-resistant'
                };
            })
            .sort((a, b) => a.binStart - b.binStart);
    }, [data?.pricing_receptiveness]);

    // Calculate statistics
    const getStats = (rawData, type) => {
        if (!rawData || rawData.length === 0) return null;
        
        // Filter out null ratings
        const validData = rawData.filter(d => d.rating !== null);
        if (validData.length === 0) return null;
        
        const ratings = validData.map(d => d.rating);
        const count = ratings.length;
        const avg = ratings.reduce((sum, r) => sum + r, 0) / count;
        
        let stats = {
            avg: type === 'credit_score' ? Math.round(avg) : avg,
            count: count,
            min: Math.min(...ratings),
            max: Math.max(...ratings),
        };

        // Add IQR and Significance for spread data
        if (type === 'interest_rate_spread' && count > 1) {
            const p25 = percentile(ratings, 25);
            const p75 = percentile(ratings, 75);
            const stdDev = Math.sqrt(ratings.reduce((sq, n) => sq + Math.pow(n - avg, 2), 0) / (count - 1));
            
            let significant = false;
            if (stdDev > 0) {
                const tStat = Math.abs(avg / (stdDev / Math.sqrt(count)));
                significant = tStat > T_CRITICAL_VALUE; 
            }

            stats = {
                ...stats,
                p25: p25,
                p75: p75,
                significant: significant,
                min: undefined, 
                max: undefined,
            };
        } else if (type === 'interest_rate_spread') {
             stats = {
                ...stats,
                p25: ratings.length > 0 ? ratings[0] : null,
                p75: ratings.length > 0 ? ratings[0] : null,
                significant: false, 
                min: undefined, 
                max: undefined,
             }
        }

        return stats;
    };

    const interestRateStats = getStats(data?.interest_rate, 'interest_rate');
    const creditScoreStats = getStats(data?.credit_score, 'credit_score');
    const spreadStats = getStats(data?.interest_rate_spread, 'interest_rate_spread');

    // Calculate statistics for pricing receptiveness
    const pricingStats = useMemo(() => {
        if (!data?.pricing_receptiveness || data.pricing_receptiveness.length === 0) return null;
        
        // Filter out null ratings
        const validData = data.pricing_receptiveness.filter(d => d.rating !== null);
        if (validData.length === 0) return null;
        
        const ratings = validData.map(d => d.rating);
        const count = ratings.length;
        const avg = ratings.reduce((sum, r) => sum + r, 0) / count;
        
        // Count items in each category
        const receptiveCount = validData.filter(d => d.rating > 3).length;
        const neutralCount = validData.filter(d => d.rating >= -3 && d.rating <= 3).length;
        const resistantCount = validData.filter(d => d.rating < -3).length;
        
        return {
            avg: avg,
            count: count,
            min: Math.min(...ratings),
            max: Math.max(...ratings),
            receptiveCount,
            neutralCount,
            resistantCount,
            receptivePercentage: (receptiveCount / count) * 100,
            neutralPercentage: (neutralCount / count) * 100,
            resistantPercentage: (resistantCount / count) * 100
        };
    }, [data?.pricing_receptiveness]);

    // --- Prepare Data for Scatter Plot --- (REVISED LOGIC)
    const scatterPlotData = useMemo(() => {
        console.log("Preparing scatter plot data (Revised Logic)...");
        console.log("Interest rate data length:", data?.interest_rate?.length || 0);
        console.log("Credit score data length:", data?.credit_score?.length || 0);
        
        // Log first few items from both datasets to confirm data structure
        if (data?.interest_rate?.length > 0) {
            console.log("First interest rate item:", JSON.stringify(data.interest_rate[0]));
            console.log("Property names in first interest rate item:", Object.keys(data.interest_rate[0]));
        }
        
        if (data?.credit_score?.length > 0) {
            console.log("First credit score item:", JSON.stringify(data.credit_score[0]));
            console.log("Property names in first credit score item:", Object.keys(data.credit_score[0]));
        }
        
        const postDataMap = new Map();

        // 1. Process interest rate data
        console.log("Processing interest rate data...");
                
        // Debug: Check the structure of interest rate items
        if (data?.interest_rate && data.interest_rate.length > 0) {
            // Check for possible alternative property names
            const sampleItem = data.interest_rate[0];
            console.log("Possible ID fields in interest rate data:", 
                        Object.keys(sampleItem).filter(key => key.toLowerCase().includes('id')));
            console.log("Possible rating fields in interest rate data:", 
                        Object.keys(sampleItem).filter(key => key.toLowerCase().includes('rate') || key.toLowerCase().includes('rating')));
        }

        let mapEntriesCount = 0;
        (data?.interest_rate || []).forEach((item, index) => {
            if (index < 5 || index === 100) { // Log first few and specifically item 100
                console.log(`Interest rate item #${index}:`, JSON.stringify(item));
                console.log(`  - Type of item:`, typeof item);
                if (item && typeof item === 'object') {
                    console.log(`  - Has id:`, 'id' in item, item.id);
                    console.log(`  - Has rating:`, 'rating' in item, item.rating);
                    console.log(`  - Type of rating:`, typeof item.rating);
                }
            }
            
            // Use 'id' instead of 'post_id' and only add non-null ratings
            if (item && typeof item === 'object' && 'id' in item && 'rating' in item && item.rating !== null) {
                postDataMap.set(item.id, {
                    ...item, // Keep all original post info
                    interest_rate: item.rating, // Store specifically named rating
                    rating: undefined // Remove generic rating key if present
                });
                mapEntriesCount++;
            }
        });
        console.log(`Map after processing interest rates: ${mapEntriesCount} entries added`);
        
        // Log map keys to confirm what's been added
        console.log("Sample of map keys after interest rate processing:", 
                   Array.from(postDataMap.keys()).slice(0, 5));
        
        // 2. Process credit score data and merge
        console.log("Processing credit score data...");
        
        // Debug: Check the structure of credit score items
        if (data?.credit_score && data.credit_score.length > 0) {
            // Check for possible alternative property names
            const sampleItem = data.credit_score[0];
            console.log("Possible ID fields in credit score data:", 
                        Object.keys(sampleItem).filter(key => key.toLowerCase().includes('id')));
            console.log("Possible rating fields in credit score data:", 
                        Object.keys(sampleItem).filter(key => key.toLowerCase().includes('score') || key.toLowerCase().includes('rating')));
        }
        
        let matchesFound = 0;
        (data?.credit_score || []).forEach((scoreItem, index) => {
            if (index < 5) { // Log first few items
                console.log(`Credit score item #${index}:`, JSON.stringify(scoreItem));
                console.log(`  - Type of item:`, typeof scoreItem);
                if (scoreItem && typeof scoreItem === 'object') {
                    console.log(`  - Has id:`, 'id' in scoreItem, scoreItem.id);
                    console.log(`  - Has rating:`, 'rating' in scoreItem, scoreItem.rating);
                    console.log(`  - Type of rating:`, typeof scoreItem.rating);
                }
                console.log(`  - Would match in map:`, postDataMap.has(scoreItem?.id));
            }
            
            // Use 'id' instead of 'post_id' and only process non-null ratings
            if (scoreItem && typeof scoreItem === 'object' && 'id' in scoreItem && 'rating' in scoreItem && scoreItem.rating !== null) {
                if (postDataMap.has(scoreItem.id)) {
                    const existingEntry = postDataMap.get(scoreItem.id);
                    postDataMap.set(scoreItem.id, {
                        ...existingEntry,
                        credit_score: scoreItem.rating, // Add the credit score rating
                    });
                    matchesFound++;
                } else {
                    // If we don't have this id yet from interest_rate data, add it
                    postDataMap.set(scoreItem.id, {
                        ...scoreItem,
                        credit_score: scoreItem.rating,
                        rating: undefined
                    });
                }
            }
        });
        console.log(`Credit score processing complete: ${matchesFound} matches found`);

        // 3. Filter map values to get only entries with both ratings
        const combinedData = Array.from(postDataMap.values()).filter(
            entry => typeof entry.interest_rate === 'number' && 
                     !isNaN(entry.interest_rate) &&
                     typeof entry.credit_score === 'number' && 
                     !isNaN(entry.credit_score)
        );

        console.log("Final combined scatter data length:", combinedData.length);
        if (combinedData.length > 0) {
            console.log("First combined data item:", JSON.stringify(combinedData[0]));
        } else {
            console.log("No combined data items found with both interest_rate and credit_score.");
            
            // Check if we have ANY entries with just interest_rate
            const interestRateOnlyCount = Array.from(postDataMap.values()).filter(
                entry => typeof entry.interest_rate === 'number' && !isNaN(entry.interest_rate)
            ).length;
            console.log(`Entries with only interest_rate: ${interestRateOnlyCount}`);
            
            // Check if we have ANY entries with just credit_score
            const creditScoreOnlyCount = Array.from(postDataMap.values()).filter(
                entry => typeof entry.credit_score === 'number' && !isNaN(entry.credit_score)
            ).length;
            console.log(`Entries with only credit_score: ${creditScoreOnlyCount}`);
        }
        
        return combinedData;
    }, [data?.interest_rate, data?.credit_score]);

    console.log("Final scatterPlotData state:", scatterPlotData); // Log the final state

    // Get the filtered posts based on selected type and bin
    const filteredPosts = useMemo(() => {
        if (selectedScatterPost) {
            // Create a formatted post object that matches the expected structure
            // from the original post data with the combined data we have
            const formattedPost = {
                ...selectedScatterPost,
                rating: selectedScatterPost.interest_rate || selectedScatterPost.credit_score
            };
            return [formattedPost];
        }

        if (!selectedType) {
            // If no type selected, show all posts with non-null ratings
            return [
                ...(data?.interest_rate?.filter(post => post.rating !== null) || []),
                ...(data?.credit_score?.filter(post => post.rating !== null) || []),
                ...(data?.interest_rate_spread?.filter(post => post.rating !== null) || [])
            ];
        }

        if (!data?.[selectedType]) return [];
        
        // Filter out null ratings
        const postsWithRatings = data[selectedType].filter(post => post.rating !== null);
        
        if (selectedBin === null) {
            return postsWithRatings;
        }
        
        const binSize = selectedType === 'credit_score' ? 50 : 100;
        return postsWithRatings.filter(post => {
            const postBin = Math.floor(post.rating / binSize) * binSize;
            return postBin === selectedBin;
        });
    }, [data, selectedType, selectedBin, selectedScatterPost]);

    const handleBarClick = (entry, type) => {
        const clickedBinStart = entry.binStart;
        setSelectedType(type);
        setSelectedBin(selectedBin === clickedBinStart && selectedType === type ? null : clickedBinStart);
        setSelectedScatterPost(null);
        setShowPostDisplay(true);
    };

    const handleScatterPointClick = (postData) => {
        setSelectedScatterPost(postData);
        setSelectedType(null);
        setSelectedBin(null);
        setShowPostDisplay(true);
    };

    const clearFilters = () => {
        setSelectedType(null);
        setSelectedBin(null);
        setSelectedScatterPost(null);
    };

    // Get appropriate rating name and format for posts display
    const getRatingInfo = () => {
        if (selectedScatterPost) {
            return { name: 'Details', format: null };
        }
        if (selectedType === 'credit_score') {
            return { name: 'Credit Score', format: 'integer' };
        } else if (selectedType === 'interest_rate_spread') {
            return { name: 'Interest Rate Spread', format: 'percentage' };
        } else if (selectedType === 'interest_rate') {
            return { name: 'Interest Rate', format: 'percentage' };
        } else if (selectedType === 'pricing_receptiveness') {
            return { name: 'Pricing Receptiveness', format: 'decimal' };
        }
        return { name: 'Rating', format: null };
    };

    const ratingInfo = getRatingInfo();

    // Determine selected bin for dimming effect
    const currentSelectedBin = selectedType ? selectedBin : null;

    // Determine if any filter is active
    const isFilterActive = selectedType !== null || selectedBin !== null || selectedScatterPost !== null;

    // Value formatters for stats and axes
    const formatPercentage = (value) => value !== null && value !== undefined ? `${Math.round(value / 100)}%` : 'N/A';
    const formatInteger = (value) => value !== null && value !== undefined ? `${Math.round(value)}` : 'N/A';
    const formatDecimal = (value) => value !== null && value !== undefined ? `${Math.round(value * 10) / 10}` : 'N/A';

    return (
        <div className="interest-rate-display">
            {console.log("Scatter plot widget render check. Data length:", scatterPlotData?.length)}
            {scatterPlotData && scatterPlotData.length > 0 && (
                <ScatterPlotWidget
                    title="Credit Score vs. Interest Rate"
                    data={scatterPlotData}
                    xKey="credit_score"
                    yKey="interest_rate"
                    xLabel="Credit Score"
                    yLabel="Interest Rate (%)"
                    pointColor="#ff7300"
                    onPointClick={handleScatterPointClick}
                    xFormatter={formatInteger}
                    yFormatter={formatPercentage}
                />
            )}

            {spreadData && (
                <div className="histogram-with-summary">
                    <h2 className="histogram-widget-title">Interest Rate Spread vs Competitors</h2>
                    
                    <div className="histogram-additional-info">
                        Positive = Higher than competitors, Negative = Lower than competitors
                    </div>
                    
                    <HistogramWidget
                        title=""
                        data={spreadData}
                        stats={spreadStats}
                        xAxisKey="spreadRange"
                        barColor={(entry) => entry.isPositive ? '#ef5350' : '#4caf50'}
                        onBarClick={(entry) => handleBarClick(entry, 'interest_rate_spread')}
                        valueFormatter={formatPercentage}
                        selectedBin={selectedType === 'interest_rate_spread' ? currentSelectedBin : null}
                        binStartKey="binStart"
                        avgLabel="Avg Spread"
                        rangeLabel="IQR (25-75%)"
                        avgColor={spreadStats?.avg !== null && spreadStats?.avg !== undefined ? (spreadStats.avg > 0 ? '#ef5350' : '#4caf50') : null}
                        hideSummaryStats={true}
                    />
                    
                    <div className="stat-summary">
                        <div className="stat-summary-item">
                            <span className="stat-label">Average Difference</span>
                            <span className="stat-value" style={{ color: spreadStats?.avg > 0 ? '#d32f2f' : '#388e3c' }}>
                                {spreadStats?.avg > 0 ? '+' : ''}{formatPercentage(spreadStats?.avg || 0)} 
                                {spreadStats?.significant ? ' *' : ''}
                            </span>
                            {spreadStats?.significant && (
                                <span className="stat-significance">Significant (p &lt; 0.05)</span>
                            )}
                        </div>
                        <div className="stat-summary-item">
                            <span className="stat-label">Higher Rate</span>
                            <span className="stat-value">
                                {spreadStats ? Math.round((spreadData.filter(item => item.binStart >= 0).reduce((sum, item) => sum + item.count, 0) / spreadStats.count) * 100) : 0}% 
                                <span className="stat-count">({spreadData ? spreadData.filter(item => item.binStart >= 0).reduce((sum, item) => sum + item.count, 0) : 0})</span>
                            </span>
                        </div>
                        <div className="stat-summary-item">
                            <span className="stat-label">Lower Rate</span>
                            <span className="stat-value">
                                {spreadStats ? Math.round((spreadData.filter(item => item.binStart < 0).reduce((sum, item) => sum + item.count, 0) / spreadStats.count) * 100) : 0}% 
                                <span className="stat-count">({spreadData ? spreadData.filter(item => item.binStart < 0).reduce((sum, item) => sum + item.count, 0) : 0})</span>
                            </span>
                        </div>
                        <div className="stat-summary-item sample-size">
                            <span className="stat-label">Sample</span>
                            <span className="stat-value">{spreadStats?.count || 0} posts</span>
                        </div>
                    </div>
                </div>
            )}

            {interestRateData && (
                <div className="histogram-with-summary">
                    <h2 className="histogram-widget-title">Interest Rates Offered</h2>
                    
                    <HistogramWidget
                        title=""
                        data={interestRateData}
                        stats={interestRateStats}
                        xAxisKey="rateRange"
                        barColor="#4caf50"
                        onBarClick={(entry) => handleBarClick(entry, 'interest_rate')}
                        valueFormatter={formatPercentage}
                        selectedBin={selectedType === 'interest_rate' ? currentSelectedBin : null}
                        binStartKey="binStart"
                        avgLabel="Avg Rate"
                        rangeLabel="Range"
                        hideSummaryStats={true}
                    />
                    
                    <div className="stat-summary">
                        <div className="stat-summary-item">
                            <span className="stat-label">Average Rate</span>
                            <span className="stat-value">{formatPercentage(interestRateStats?.avg || 0)}</span>
                        </div>
                        <div className="stat-summary-item">
                            <span className="stat-label">Range</span>
                            <span className="stat-value">
                                {formatPercentage(interestRateStats?.min || 0)} - {formatPercentage(interestRateStats?.max || 0)}
                            </span>
                        </div>
                        <div className="stat-summary-item sample-size">
                            <span className="stat-label">Sample</span>
                            <span className="stat-value">{interestRateStats?.count || 0} posts</span>
                        </div>
                    </div>
                </div>
            )}

            {creditScoreData && (
                <div className="histogram-with-summary">
                    <h2 className="histogram-widget-title">Credit Scores Mentioned</h2>
                    
                    <HistogramWidget
                        title=""
                        data={creditScoreData}
                        stats={creditScoreStats}
                        xAxisKey="scoreRange"
                        barColor="#2196f3"
                        onBarClick={(entry) => handleBarClick(entry, 'credit_score')}
                        valueFormatter={formatInteger}
                        selectedBin={selectedType === 'credit_score' ? currentSelectedBin : null}
                        binStartKey="binStart"
                        avgLabel="Avg Score"
                        rangeLabel="Range"
                        hideSummaryStats={true}
                    />
                    
                    <div className="stat-summary">
                        <div className="stat-summary-item">
                            <span className="stat-label">Average Score</span>
                            <span className="stat-value">{formatInteger(creditScoreStats?.avg || 0)}</span>
                        </div>
                        <div className="stat-summary-item">
                            <span className="stat-label">Range</span>
                            <span className="stat-value">
                                {formatInteger(creditScoreStats?.min || 0)} - {formatInteger(creditScoreStats?.max || 0)}
                            </span>
                        </div>
                        <div className="stat-summary-item sample-size">
                            <span className="stat-label">Sample</span>
                            <span className="stat-value">{creditScoreStats?.count || 0} posts</span>
                        </div>
                    </div>
                </div>
            )}

            {pricingData && (
                <div className="histogram-with-summary">
                    <h2 className="histogram-widget-title">Pricing Receptiveness</h2>
                    
                    <div className="histogram-additional-info">
                        Positive values indicate openness to price increases, negative values indicate price sensitivity
                    </div>
                    
                    <HistogramWidget
                        title=""
                        data={pricingData}
                        stats={pricingStats}
                        xAxisKey="receptivenessRange"
                        barColor={(entry) => 
                            entry.sentiment === 'very-receptive' ? '#388e3c' :
                            entry.sentiment === 'receptive' ? '#4caf50' :
                            entry.sentiment === 'neutral' ? '#90a4ae' :
                            entry.sentiment === 'resistant' ? '#ef5350' : '#d32f2f'
                        }
                        onBarClick={(entry) => handleBarClick(entry, 'pricing_receptiveness')}
                        valueFormatter={formatDecimal}
                        selectedBin={selectedType === 'pricing_receptiveness' ? currentSelectedBin : null}
                        binStartKey="binStart"
                        avgLabel="Avg Score"
                        rangeLabel="Range"
                        hideSummaryStats={true}
                    />
                    
                    <div className="stat-summary">
                        <div className="stat-summary-item">
                            <span className="stat-label">Average Score</span>
                            <span className="stat-value">{formatDecimal(pricingStats?.avg || 0)}</span>
                        </div>
                        <div className="stat-summary-item">
                            <span className="stat-label">Open to Increase</span>
                            <span className="stat-value" style={{ color: '#388e3c' }}>
                                {Math.round(pricingStats?.receptivePercentage || 0)}% 
                                <span className="stat-count">({pricingStats?.receptiveCount || 0})</span>
                            </span>
                        </div>
                        <div className="stat-summary-item">
                            <span className="stat-label">Price Sensitive</span>
                            <span className="stat-value" style={{ color: '#d32f2f' }}>
                                {Math.round(pricingStats?.resistantPercentage || 0)}% 
                                <span className="stat-count">({pricingStats?.resistantCount || 0})</span>
                            </span>
                        </div>
                        <div className="stat-summary-item sample-size">
                            <span className="stat-label">Sample</span>
                            <span className="stat-value">{pricingStats?.count || 0} posts</span>
                        </div>
                    </div>
                </div>
            )}

            {isFilterActive && (
                <div className="filter-notice">
                    <span>
                        {selectedScatterPost 
                            ? `Viewing details for: "${selectedScatterPost.title || 'Selected Post'}"` 
                            : selectedType 
                                ? (
                                    selectedBin !== null ? (
                                        `Showing ${filteredPosts.length} posts with ${ratingInfo.name} in the ${
                                            selectedType === 'credit_score' ? 
                                                `${formatInteger(selectedBin)} - ${formatInteger(selectedBin + 50)}` : 
                                            selectedType === 'pricing_receptiveness' ?
                                                `${selectedBin} to ${selectedBin + 2}` :
                                                `${Math.round(selectedBin/100)}% - ${Math.round((selectedBin + 100)/100)}%`
                                        } range`
                                    ) : (
                                        `Showing ${filteredPosts.length} posts with ${ratingInfo.name} data`
                                    )
                                ) 
                                : ''}
                    </span>
                    <button onClick={clearFilters} className="clear-filter">
                        Clear Filter
                    </button>
                </div>
            )}

            {showPostDisplay && filteredPosts.length > 0 && (
                <FilteredPostsDisplay 
                    posts={filteredPosts}
                    onClose={() => setShowPostDisplay(false)}
                    id="interest-rate-posts"
                    title={selectedScatterPost 
                        ? `Details for Selected Post` 
                        : selectedType 
                            ? `Posts with ${ratingInfo.name} Data` 
                            : "Filtered Interest Rate Analysis Posts"
                    }
                    ratingDisplayName={selectedScatterPost ? null : ratingInfo.name} 
                    autoScroll={true} 
                    ratingColorScheme={selectedScatterPost ? null : (selectedType === 'interest_rate_spread' ? { high: 0, low: 0 } : null)}
                    showSortControls={!selectedScatterPost} 
                    ratingFormat={selectedScatterPost ? null : ratingInfo.format} 
                />
            )}
        </div>
    );
};

export default InterestRateDisplay;
